import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authService } from "../services/authService";

// Session state for the whole app. The source of truth is the httpOnly session
// cookie — this context only mirrors it via GET /auth/me/ (no tokens in JS).

const AuthContext = createContext({
  user: null,
  isLoading: true,
  refresh: async () => {},
  signOut: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await authService.me();
      setUser(data.authenticated ? data.user : null);
      return data.authenticated ? data.user : null;
    } catch {
      setUser(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await authService.signOut();
    } finally {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    // Prime the CSRF cookie first so every later POST carries a valid token.
    authService.primeCsrf().then(refresh);
  }, [refresh]);

  const value = useMemo(
    () => ({ user, isLoading, refresh, signOut }),
    [user, isLoading, refresh, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
